import React, { useEffect, useState } from 'react';
import CurrentlyViewing from '@/components/CurrentlyViewing';
import { useSocialProofEnabled } from '@/hooks/useSocialProofEnabled';

/**
 * Thin floating wrapper around the existing CurrentlyViewing badge. Anchors
 * the live viewers social-proof badge to a fixed position on the page so
 * visitors see it regardless of scroll depth. Appears after a short delay so
 * it feels organic rather than flashing on first paint.
 *
 * Position presets: "top-left" | "top-right" | "bottom-left" | "bottom-right"
 * Visible only after user has scrolled a little so it doesn't cover the hero.
 */
export const FloatingSocialProof = ({ position = 'top-right', offsetBottomMd = 40 }) => {
  const enabled = useSocialProofEnabled();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 2200);
    const onScroll = () => {
      if (window.scrollY > 250) setShow(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (!enabled) return null;
  if (!show) return null;

  const positionClass = {
    'top-left': 'top-44 md:top-48 left-3 md:left-6',
    'top-right': 'top-44 md:top-48 right-3 md:right-6',
    'bottom-left': 'bottom-44 md:bottom-44 left-3 md:left-6',
    'bottom-right': 'bottom-44 md:bottom-44 right-3 md:right-6',
  }[position] || 'bottom-44 md:bottom-44 left-3 md:left-6';

  return (
    <div
      className={`fixed ${positionClass} z-30 pointer-events-none`}
      data-testid="floating-social-proof"
    >
      <div className="pointer-events-auto bg-white/95 backdrop-blur-sm border border-emerald-200 shadow-lg rounded-full pl-0.5 pr-3 py-1 flex items-center">
        <CurrentlyViewing variant="badge" />
      </div>
    </div>
  );
};

export default FloatingSocialProof;
